import React, { useEffect, useMemo, useState } from "react";
import { DA_ProductItemSlider } from "@organisms/ProductItemSlider";
import {
    PersonalProductRecommendationBuilder,
    RecentlyViewedProductsBuilder,
    PopularProductsBuilder,
    ProductResult,
} from "@relewise/client";
import { useRelewise } from "@danishagro/shared/src/contexts/relewise.context";
import { addWebUserGroupsFilter } from "@danishagro/shared/src/helpers/relewiseFilters.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";

export interface V1Props {
    headline: string;
    sliderType: "Latest" | "Popular" | "Personal";
}

export const V1: React.FC<V1Props> = ({ headline, sliderType }) => {
    const { getRecommender, mapProduct, relewiseUser } = useRelewise();
    const { customerNumber, cvrAndCustomerNumbers } = useAppData();
    const [products, setProducts] = useState<ProductResult[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // ✅ Memoize settings to prevent unnecessary re-renders
    const settings = useMemo(
        () => ({
            language: "da",
            currency: "DKK",
            displayedAtLocation: globalThis.pageAlias || globalThis.alias,
            user: relewiseUser(),
        }),
        [relewiseUser]
    );

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const recommender = getRecommender();
            if (!recommender) {
                setLoading(false);
                return;
            }

            let builder;
            try {
                switch (sliderType) {
                    case "Popular": {
                        const popularBuilder = new PopularProductsBuilder(settings)
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setNumberOfRecommendations(6)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .basedOn("MostViewed")
                            .sinceMinutesAgo(144000)
                            .prioritizeDiversityBetweenRequests(false);

                        if (globalThis.useCustomerGroups) {
                            popularBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }
                        builder = popularBuilder.build();
                        const result = await recommender.recommendPopularProducts(builder);
                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    case "Personal": {
                        if (!globalThis.isAuthenticated) {
                            setLoading(false);
                            return;
                        }

                        const personalBuilder = new PersonalProductRecommendationBuilder(settings)
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setNumberOfRecommendations(10)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .prioritizeDiversityBetweenRequests(false);

                        if (globalThis.useCustomerGroups) {
                            personalBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }
                        builder = personalBuilder.build();
                        const result = await recommender.recommendPersonalProducts(builder);
                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    case "Latest": {
                        if (!globalThis.isAuthenticated) {
                            setLoading(false);
                            return;
                        }

                        const recentlyViewedBuilder = new RecentlyViewedProductsBuilder(settings)
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setNumberOfRecommendations(6)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .prioritizeDiversityBetweenRequests(false);

                        if (globalThis.useCustomerGroups) {
                            recentlyViewedBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }
                        builder = recentlyViewedBuilder.build();
                        const result = await recommender.recentlyViewedProducts(builder);
                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    default:
                        setLoading(false);
                        return;
                }
            } catch (error) {
                console.error(`API Error (${sliderType}):`, error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [sliderType, getRecommender, settings, customerNumber, cvrAndCustomerNumbers]);

    // Display a loading indicator while fetching data
    if (loading) return <div />;

    // Return null if no products exist
    if (!products.length) return null;

    return (
        <DA_ProductItemSlider
            title={headline}
            items={products.map((product) => mapProduct(product))}
        />
    );
};
